import React, { useState } from "react";
import { Button, Icon, IconButton, TextField } from "stories/Admin";
import {
  MAX_CATEGORIES,
  MAX_CATEGORY_LENGTH,
  addCategory,
  moveCategory,
  remainingSuggestions,
  removeCategory,
} from "./campaignCategories";
import styles from "./CampaignPage.module.scss";

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  /** Feil fra skjemavalideringen. */
  error?: string;
}

/** «Kategorier (valgfritt)»: brikker med fjern-knapp, eget felt og hurtigvalg. */
const CategoryEditor: React.FC<Props> = ({ value, onChange, error }) => {
  const [text, setText] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const full = value.length >= MAX_CATEGORIES;

  const add = (name: string) => {
    const result = addCategory(value, name);
    setAddError(result.error);
    if (!result.error) {
      onChange(result.list);
      if (name === text) setText("");
    }
  };

  const suggestions = remainingSuggestions(value);

  return (
    <div className={styles.categories}>
      <span className={styles.groupLabel}>Kategorier (valgfritt)</span>
      {value.length > 0 && (
        <ul className={styles.categoryList} aria-label="Kategorier">
          {value.map((c, i) => (
            <li key={c} className={styles.categoryChip}>
              <span className={styles.categoryName}>{c}</span>
              {value.length > 1 && (
                <>
                  <IconButton
                    size="sm"
                    label={`Flytt «${c}» opp`}
                    disabled={i === 0}
                    icon={
                      <Icon name="chevronLeft" size={14} strokeWidth={2.4} />
                    }
                    onClick={() => onChange(moveCategory(value, i, -1))}
                  />
                  <IconButton
                    size="sm"
                    label={`Flytt «${c}» ned`}
                    disabled={i === value.length - 1}
                    icon={
                      <Icon name="chevronRight" size={14} strokeWidth={2.4} />
                    }
                    onClick={() => onChange(moveCategory(value, i, 1))}
                  />
                </>
              )}
              <IconButton
                size="sm"
                label={`Fjern «${c}»`}
                icon={<Icon name="x" size={14} strokeWidth={2.4} />}
                onClick={() => {
                  setAddError(null);
                  onChange(removeCategory(value, c));
                }}
              />
            </li>
          ))}
        </ul>
      )}
      <div className={styles.categoryAdd}>
        <TextField
          aria-label="Legg til kategori"
          placeholder={full ? "Maks antall nådd" : "Legg til kategori"}
          value={text}
          maxLength={MAX_CATEGORY_LENGTH + 10}
          disabled={full}
          fieldClassName={styles.categoryInput}
          onChange={(e) => {
            setText(e.target.value);
            setAddError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // Ikke send inn skjemaet.
              e.preventDefault();
              if (text.trim()) add(text);
            }
          }}
          error={addError ?? undefined}
        />
        <Button
          variant="secondary"
          disabled={full || !text.trim()}
          icon={<Icon name="plus" size={15} strokeWidth={2.2} />}
          onClick={() => add(text)}
        >
          Legg til
        </Button>
      </div>
      {!full && suggestions.length > 0 && (
        <div className={styles.suggestions} aria-label="Forslag">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              className={styles.suggestion}
              onClick={() => add(s)}
            >
              <Icon name="plus" size={12} strokeWidth={2.6} />
              {s}
            </button>
          ))}
        </div>
      )}
      <span className={styles.help}>
        Giveren velger én kategori før betaling. Du ser hvor mye som er samlet
        inn i hver kategori under Innsamlet.
      </span>
      {error && (
        <span className={styles.error} role="alert">
          {error}
        </span>
      )}
    </div>
  );
};

export default CategoryEditor;
